/**
 * Розкладка сітки записів (UIUX tables/table-view; прототип v4 BASE · MW · W · fitCols · ordDef): ширини стовпців за типом і підписом,
 * доріжки grid-template-columns, мінімальна ширина сітки, стовпці за замовчуванням, що вміщуються за шириною. Чисті функції.
 */
import type { IFieldDefinition } from '@platform/core/types';
import { columnMinWidth } from '../columnMinWidth';
import type { IGridColumn } from './IGridColumn';

/** Службові стовпці зліва (`lead`): прапорець вибору 40px і код 92px; назва (`titleMin`) — від 220px (minmax(220px, 2.2fr)). */
export const GRID_SIZES = { lead: 132, titleMin: 220 } as const;
const TITLE_TRACK = 'minmax(220px, 2.2fr)';

/** Типи, стовпці яких розтягуються (minmax(MW, 1fr)); решта — рівно MW. */
const FLEX_TYPES: readonly string[] = ['text', 'textarea', 'tags', 'area', 'iteration'];

/**
 * Ширина для стовпців полів за замовчуванням (прототип v4 fitW, `width` — ширина основної області сторінки): прототип рахує від вікна, віднімаючи
 * лише меню (248), відкриті панелі, поля сторінки (2 × 36) і службові стовпці (132), але не менше 640 разом із назвою (220) — тож на вузькому
 * екрані сітка прокручується вбік, а не губить виконавця. Наша основна область — без полів 2 × 28, тож вона на 16px ширша за прототипну.
 */
export function fitBudget(width: number): number {
  return Math.max(640, width - 16 - GRID_SIZES.lead) - GRID_SIZES.titleMin;
}

/** Пріоритет стовпців, що лишаються, коли не все вміщується (назва — завжди): статус, виконавець, термін, пріоритет, важливість, теги, напрям, ітерація. */
const PRIORITY: readonly string[] = ['status', 'assignee', 'due', 'priority', 'severity', 'tags', 'area', 'iteration'];

/**
 * Стовпець поля: ширина max(база типу, підпис × 7.6 + 64) за текстом `fitText` (типово — англійський підпис визначення, як рахує прототип v4,
 * без нього — підпис мовою інтерфейсу; перейменоване поле — новий підпис, fitLabelOf), тож набір стовпців, що вміщуються, не залежить від
 * мови; доріжка — гнучка для текстових типів.
 */
export function gridColumn(field: IFieldDefinition, label: string, fitText: string = field.label.en?.trim() || label): IGridColumn {
  const width = columnMinWidth(field.type, fitText);
  return { field, label, width, track: FLEX_TYPES.includes(field.type) ? `minmax(${width}px, 1fr)` : `${width}px` };
}

/** grid-template-columns: [прапорець і код,] назва, стовпці полів. */
export function gridTemplate(columns: readonly IGridColumn[], lead = true): string {
  return [...(lead ? ['40px', '92px'] : []), TITLE_TRACK, ...columns.map((c) => c.track)].join(' ');
}

/** Мінімальна ширина сітки (px): службові стовпці + назва + мінімуми стовпців — вужчий контейнер прокручує сітку вбік. */
export function gridMinWidth(columns: readonly IGridColumn[], lead = true): number {
  return (lead ? GRID_SIZES.lead : 0) + GRID_SIZES.titleMin + columns.reduce((sum, c) => sum + c.width, 0);
}

/**
 * Стовпці за замовчуванням, що вміщуються в `available` px (ширина для стовпців полів, без службових і назви): за пріоритетом PRIORITY,
 * далі в порядку визначення; стовпець, що не вміщується, пропускається (наступні вужчі ще можуть вміститися). Порядок результату — як
 * у прототипі: виконавець першим, далі решта в порядку визначення.
 */
export function fitColumns(columns: readonly IGridColumn[], available: number): IGridColumn[] {
  const rank = (c: IGridColumn, i: number) => (PRIORITY.includes(c.field.key) ? PRIORITY.indexOf(c.field.key) : 50 + i);
  const order = columns.map((c, i) => ({ c, r: rank(c, i) })).sort((a, b) => a.r - b.r);
  const keep = new Set<string>();
  let used = 0;
  for (const { c } of order) {
    if (used + c.width > available) continue;
    keep.add(c.field.key);
    used += c.width;
  }
  return assigneeFirst(columns.filter((c) => keep.has(c.field.key)));
}

/** Порядок стовпців за замовчуванням (прототип ordDef): виконавець першим, далі решта в порядку визначення. */
export function assigneeFirst(columns: readonly IGridColumn[]): IGridColumn[] {
  return [...columns.filter((c) => c.field.key === 'assignee'), ...columns.filter((c) => c.field.key !== 'assignee')];
}
